import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { resetHolidayOverride } from '../dataProvider';

export interface ResetOverrideDialogProps {
    open: boolean;
    /** The `YYYY-MM-DD` date whose override should be reset, or `null` when closed. */
    date: string | null;
    holidayName: string;
    onClose: () => void;
    /** Called after a successful reset so the caller can refetch the list. */
    onSuccess: () => void;
}

/**
 * Confirmation dialog for resetting a holiday override, restoring the date
 * as a public holiday. Calls `DELETE /holidays/:date/override` via
 * {@link resetHolidayOverride}.
 */
export const ResetOverrideDialog: React.FC<ResetOverrideDialogProps> = ({
    open,
    date,
    holidayName,
    onClose,
    onSuccess,
}) => {
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    /** Closes the dialog, ignoring the request while a reset is in flight. */
    const handleClose = () => {
        if (loading) return;
        onClose();
    };

    /**
     * Confirms the reset, calling {@link resetHolidayOverride} and notifying
     * the user of the outcome before invoking `onSuccess`.
     */
    const handleConfirm = async () => {
        if (!date) return;

        setLoading(true);
        try {
            await resetHolidayOverride(date);
            notify('Holiday override reset successfully', { type: 'success' });
            onSuccess();
        } catch (error: any) {
            const errorMessage = error?.body?.message || error?.message || 'Failed to reset holiday override';
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Reset Override for {holidayName || date}?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This will restore {date} as a public holiday.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading}
                    data-testid="reset-confirm-btn"
                >
                    {loading ? <CircularProgress size={20} /> : 'Reset'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
